import { useCallback, useEffect, useImperativeHandle, useLayoutEffect, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import useCardRefs from "@/lib/use_card_refs";

// Persistent card stack — the "card shuffle" the owner asked for.
//
// The deck no longer swaps the top card the instant a swipe commits. Every
// card in the window (top + 2 peeks) is the SAME full card component, keyed
// by its stable listing id, so when the deck advances React only re-props the
// existing elements: depth 1 becomes depth 0 with zero remount, its scale
// simply continues from wherever the live drag left it (peek handoff), and
// the committed card stays mounted with pointer-events none while its
// fly-out finishes (useSwipePhysics calls onExited on animation complete,
// only then does it unmount). The new top is interactive IMMEDIATELY —
// buttons resolve through the id-keyed ref map and the drag target follows
// the deck order, never the unmount timing.
//
// Undo: the parent re-fronts the item; if the card already unmounted it
// remounts with enterFrom (reverse-swipe entrance), if it is still mid
// flight the physics rescue effect springs it home from where it is.

const DEPTH_SCALE = [1, 0.95, 0.92];
const PROMOTE = { duration: 0.25, ease: "easeOut" }; // depth-to-depth glide

// Module-level so the "teach the gesture" preview plays exactly ONCE per page
// load — not on every deck mount, re-render, filter change, or return from the
// popup. The two primary swipe entries (landing + /swipe) share this flag.
let demoPreviewPlayed = false;

const minDim = () => Math.min(window.innerWidth, window.innerHeight);

function useLg() {
  const [lg, setLg] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setLg(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return lg;
}

function StackCard({ depth, flying, mirrorX, pose, zIndex, cardId, veilClass = "rounded-3xl", children }) {
  // Per-card scale motion value: depth transitions animate it, and while the
  // card sits at depth 1 it tracks the top card's live drag (0.95 -> 1.0 at
  // minDim / 2, per spec §1.8). Because promotion animates FROM the current
  // value, the peek-to-top handoff can never snap.
  const scale = useMotionValue(DEPTH_SCALE[Math.min(depth, 2)]);
  const veil = useMotionValue(depth === 0 ? 0 : 0.55);

  useLayoutEffect(() => {
    if (flying) return; // frozen — the physics fly-out moves the card itself
    const anims = [
      animate(scale, DEPTH_SCALE[Math.min(depth, 2)], PROMOTE),
      animate(veil, depth === 0 ? 0 : 0.55, PROMOTE),
    ];
    let unsub;
    if (depth === 1 && mirrorX) {
      unsub = mirrorX.on("change", (m) => {
        if (!m) return; // idle / reset — let the depth glide finish instead
        anims[0].stop();
        scale.set(0.95 + 0.05 * Math.min(1, (2 * Math.abs(m)) / minDim()));
      });
    }
    return () => {
      anims.forEach((a) => a.stop());
      unsub?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depth, flying]);

  const p = (pose && !flying ? pose(Math.min(depth, 2)) : null) || {};

  return (
    <motion.div
      className="absolute inset-0"
      data-card-id={cardId}
      data-depth={flying ? "exiting" : depth}
      style={{ scale, zIndex, pointerEvents: flying ? "none" : "auto" }}
      animate={flying ? undefined : { x: p.x || 0, y: p.y || 0, rotate: p.rotate || 0 }}
      transition={PROMOTE}
    >
      {children}
      {/* Depth veil — fades away as the card is promoted */}
      <motion.div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 bg-night ${veilClass}`}
        style={{ opacity: veil }}
      />
    </motion.div>
  );
}

export default function CardStack({
  deck, // ordered items; deck[0] is the live top (advance = remove / shift)
  visible = 3, // cards in the DOM window (top + peeks)
  onSwipe, // (action, item) -> false when a gate vetoed the swipe
  renderCard, // (item, cardProps) -> the surface's card component
  pose, // (depth, lg) -> { x?, y?, rotate? } resting pose per depth
  undone = null, // { id, dir } — a restored card enters from its exit vector
  demoPreview = false, // the two primary entries teach the gesture once per load
  veilClass = "rounded-3xl", // depth-veil rounding must match the card surface
  ref,
}) {
  // Committed cards mid fly-out: they left the deck already but stay mounted
  // (and painted on top) until their exit animation completes.
  const [flying, setFlying] = useState([]);
  const { bindCard, getCard } = useCardRefs();
  const lg = useLg();
  // The top card mirrors its drag magnitude here so the peek card can breathe.
  const topX = useMotionValue(0);

  const win = deck.slice(0, visible);
  const top = win[0];

  // One-shot gesture preview on the top card the first time a primary deck
  // paints on a fresh load. Guarded by a module-level flag (never replays on
  // re-render, filter change, or return from the popup) and gated to the two
  // primary swipe entries via the demoPreview prop. The physics hook itself
  // skips when prefers-reduced-motion is set and bails if the user interacts.
  useEffect(() => {
    if (!demoPreview || demoPreviewPlayed || !top) return;
    demoPreviewPlayed = true;
    // Let the top card's imperative handle register first.
    const id = setTimeout(() => getCard(top.id)?.preview?.(), 260);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demoPreview, top?.id]);

  const handleSwipe = useCallback(
    (action, item) => {
      const ok = onSwipe(action, item);
      if (ok === false) return false;
      setFlying((f) => (f.some((x) => x.id === item.id) ? f : [...f, item]));
      return true;
    },
    [onSwipe]
  );

  const handleExited = useCallback((id) => {
    setFlying((f) => f.filter((x) => x.id !== id));
  }, []);

  // Undo can pull a still-flying card back into the window: the window copy
  // wins (same key, the instance is rescued by the physics hook) and the
  // flying entry is dropped.
  useEffect(() => {
    const ids = new Set(win.map((d) => d.id));
    setFlying((f) => (f.some((x) => ids.has(x.id)) ? f.filter((x) => !ids.has(x.id)) : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deck, visible]);

  useImperativeHandle(
    ref,
    () => ({
      // Buttons and keyboard run the exact same fly-out as a drag via the
      // id-keyed handle; the direct fallback means a click is never dropped.
      swipeTop: (action) => {
        if (!top) return false;
        const card = getCard(top.id);
        if (card) return card.swipe(action);
        return handleSwipe(action, top) !== false;
      },
    }),
    [top, getCard, handleSwipe]
  );

  const poseFor = pose ? (depth) => pose(depth, lg) : null;

  // ONE keyed list (flying + window) so React moves instances between the
  // two groups instead of remounting them — continuity depends on this.
  const rendered = [
    ...flying
      .filter((item) => !win.some((d) => d.id === item.id))
      .map((item) => ({ item, fly: true, depth: 0 })),
    ...win.map((item, i) => ({ item, fly: false, depth: i })),
  ];

  return (
    <>
      {rendered.map(({ item, fly, depth }) => (
        <StackCard
          key={item.id}
          cardId={item.id}
          depth={depth}
          flying={fly}
          mirrorX={topX}
          pose={poseFor}
          zIndex={fly ? 30 : 20 - depth}
          veilClass={veilClass}
        >
          {renderCard(item, {
            active: !fly && depth === 0,
            ref: fly ? undefined : bindCard(item.id),
            mirrorX: topX,
            onSwipe: (action) => handleSwipe(action, item),
            onExited: () => handleExited(item.id),
            enterFrom: !fly && depth === 0 && undone && undone.id === item.id ? undone.dir : 0,
          })}
        </StackCard>
      ))}
    </>
  );
}
